import { useState, type MouseEvent, type SVGProps } from 'react';
import { Badge, Button, Card, Input } from '@indipia/ui-core';
import type { PaymentMethod } from '../api/billingApi';
import { PaymentMethodCardBody, paymentMethodDisplayName } from './PaymentMethodCardBody';

/** ui-core `icons.tsx`에 편집(연필) 아이콘이 없어(D-07 §20 공용 아이콘 목록에 미포함) billing
 *  자체에서 로컬로 그림 — 공용 아이콘 세트와 동일한 규약(48×48 viewBox, stroke=currentColor)만
 *  맞춰서 나중에 ui-core에 정식 편입되더라도 시각적 차이가 없게 함. 추후작업_TODO.md 참고. */
function IconPencil(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={48}
      height={48}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M34 6a5.7 5.66 0 1 1 8 8L15 41 4 44l3-11Z" />
    </svg>
  );
}

export interface PaymentMethodTileProps {
  method: PaymentMethod;
  onSetDefault: () => void;
  onDelete: () => void;
  onSaveAlias: (cardAlias: string) => void;
  settingDefault: boolean;
  savingAlias: boolean;
  /** 이번 결제에 쓸 카드로 선택됐음을 테두리로 표시(C-11, 2026-09-17 사용자 요청) — 결제수단 관리
   *  화면(PaymentMethodPage)에선 이 프롭 자체를 안 넘겨 기존처럼 "기본" 배지만 보인다. */
  selected?: boolean;
  /** 카드 클릭 시 "이번 결제에 쓸 카드"로 선택(C-11 전용, 2026-09-18 사용자 결정 — "자동결제는
   *  기본카드로, 결제창에서 결제하는 순간은 선택한 카드로". 기본카드 변경(onSetDefault)과는 별개
   *  동작이라 이 카드를 눌러도 IS_DEFAULT는 안 바뀐다). 결제수단 관리 화면(PaymentMethodPage)에선
   *  이 프롭을 안 넘겨 카드 자체는 클릭 불가능한 채로 유지한다. */
  onSelect?: () => void;
}

/**
 * 결제수단 타일 — 별칭 수정·기본 설정·삭제까지 전부 갖춘 완전한 형태. 결제수단 관리 화면
 * (PaymentMethodPage)에서 처음 만들어졌고, 결제 확인 화면(CheckoutPage)도 동일하게 쓴다
 * (2026-09-16, 사용자 요청 — "이번 결제에 쓸 카드를 고른다"는 곧 그 카드를 기본 결제수단으로
 * 바꾸는 것과 같은 동작(BE는 항상 기본 결제수단으로만 청구)이라, 관리 화면과 다른 축소판 타일을
 * 따로 둘 이유가 없었다). 삭제는 호출부가 확인 모달을 띄우는 자리이므로 onDelete는 즉시 삭제가
 * 아니라 "삭제 확인을 시작하라"는 신호로만 쓴다(두 화면 모두 동일 관례).
 * <p>
 * 카드 유효기간·소유자 성명(카드에 인쇄된 이름)은 TU_PAYMENT_METHOD에 저장 컬럼 자체가 없어(910
 * 문서·실물 불일치, 추후작업_TODO.md 참고) 표시하지 않는다 — 카드사명·개인/법인 구분·은행명은
 * 표시한다(PaymentMethodCardBody 참고).
 */
export function PaymentMethodTile({ method, onSetDefault, onDelete, onSaveAlias, settingDefault, savingAlias, selected, onSelect }: PaymentMethodTileProps) {
  const [editingAlias, setEditingAlias] = useState(false);
  const [aliasDraft, setAliasDraft] = useState(method.cardAlias ?? '');

  const startEdit = () => {
    setAliasDraft(method.cardAlias ?? '');
    setEditingAlias(true);
  };

  const handleSave = () => {
    onSaveAlias(aliasDraft.trim());
    setEditingAlias(false);
  };

  // 카드 안의 버튼·입력을 눌렀을 때 그 클릭이 카드 자체의 onSelect까지 같이 타지 않도록 막는다
  // (예: "삭제"를 눌렀는데 카드가 결제용으로 선택돼버리는 것 방지).
  const stopSelect = (e: MouseEvent) => onSelect && e.stopPropagation();

  return (
    <Card
      className={`w-64 shrink-0 ${selected ? 'border-group ring-1 ring-group' : ''} ${onSelect ? 'cursor-pointer' : ''}`}
      onClick={onSelect}
    >
      {/* 카드 전체 클릭(위 onClick)은 마우스 사용자를 위한 편의 단축키일 뿐이다 — 카드 자체에
          role="button"/tabIndex를 주지 않는다. 안에 실제 버튼·입력(별칭 수정, 기본으로 설정, 삭제)이
          이미 들어있어서, 카드까지 인터랙티브 요소로 선언하면 "인터랙티브 안에 인터랙티브가 중첩"되는
          접근성 안티패턴이 된다(2026-09-18 검수 지적). 대신 아래 별도의 선택 버튼 하나가 키보드·
          스크린리더 사용자를 위한 진짜(유일한) 선택 조작 지점이다. */}
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-xs">
          {onSelect && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 px-0"
              onClick={(e) => { stopSelect(e); onSelect(); }}
              aria-pressed={!!selected}
              aria-label={
                selected
                  ? `${paymentMethodDisplayName(method)} 선택됨 — 이 카드로 결제해요`
                  : `${paymentMethodDisplayName(method)}로 결제하기`
              }
            >
              <span
                aria-hidden
                className={`block h-4 w-4 rounded-full border-2 ${selected ? 'border-group bg-group' : 'border-border'}`}
              />
            </Button>
          )}
          <span className="truncate text-body-sm font-semibold text-text">
            {paymentMethodDisplayName(method)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 px-0"
            onClick={(e) => { stopSelect(e); startEdit(); }}
            aria-label={method.cardAlias ? '별칭 수정' : '별칭 설정'}
          >
            <IconPencil width={14} height={14} />
          </Button>
        </div>
        {method.isDefault && <Badge status="success">기본</Badge>}
      </div>

      {editingAlias && (
        <div className="mt-02 flex items-center gap-xs" onClick={stopSelect}>
          <Input
            width="fill"
            value={aliasDraft}
            onChange={(e) => setAliasDraft(e.target.value)}
            placeholder="예: 회사 법인카드"
            maxLength={50}
          />
          <Button variant="ghost" size="sm" onClick={handleSave} loading={savingAlias} disabled={savingAlias}>
            저장
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditingAlias(false)}>
            취소
          </Button>
        </div>
      )}

      <div className="mt-03">
        <PaymentMethodCardBody method={method} />
      </div>

      <div className="mt-04 flex items-center justify-between text-body-sm">
        {!method.isDefault ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { stopSelect(e); onSetDefault(); }}
            loading={settingDefault}
            disabled={settingDefault}
          >
            기본으로 설정
          </Button>
        ) : (
          <span />
        )}
        <Button variant="ghost" size="sm" className="text-danger" onClick={(e) => { stopSelect(e); onDelete(); }}>
          삭제
        </Button>
      </div>
    </Card>
  );
}
